import { PropsWithChildren, createContext, useContext, useMemo, useState } from 'react';
import { Location } from '~/services/types';

interface LocationValues {
  locationId: number;
  locationTime: {
    location_current_time: string;
    location_time_offset: string;
    location_timezone: string;
  };
  isKiosk: boolean;
  isDea: boolean;
  businessSettings: {
    sessionAccessEnabled: boolean;
  };
  isSessionAccess: boolean;
  setLocationId: (value: number) => void;
  setIsKiosk: (value: boolean) => void;
  setIsDea: (value: boolean) => void;
  setIsSessionAccess: (value: boolean) => void;
}
const LocationContext = createContext<LocationValues>({
  locationId: NaN,
  locationTime: {
    location_current_time: '',
    location_time_offset: '',
    location_timezone: '',
  },
  isKiosk: false,
  isDea: false,
  businessSettings: {
    sessionAccessEnabled: false,
  },
  isSessionAccess: false,
  setLocationId: () => {},
  setIsKiosk: () => {},
  setIsDea: () => {},
  setIsSessionAccess: () => {},
});

export const LocationProvider = ({ defaultLocation, children }: PropsWithChildren<{ defaultLocation: Location }>) => {
  const [locationId, setLocationId] = useState<number>(defaultLocation.location_id);
  const [isKiosk, setIsKiosk] = useState<boolean>(false);
  const [isDea, setIsDea] = useState<boolean>(Boolean(defaultLocation.is_dea));
  const [isSessionAccess, setIsSessionAccess] = useState<boolean>(Boolean(defaultLocation.class_access_group));

  const value = useMemo(
    () => ({
      locationId,
      setLocationId,
      isKiosk,
      setIsKiosk,
      isDea,
      setIsDea,
      isSessionAccess,
      setIsSessionAccess,
    }),
    [locationId, setLocationId, isKiosk, setIsKiosk, isDea, setIsDea, isSessionAccess, setIsSessionAccess],
  );

  return (
    <LocationContext.Provider
      value={{
        locationId: value.locationId,
        setLocationId: value.setLocationId,
        locationTime: defaultLocation.location_time_info,
        isKiosk: value.isKiosk ?? false,
        setIsKiosk: value.setIsKiosk,
        isDea: value.isDea ?? false,
        setIsDea: value.setIsDea,
        businessSettings: {
          sessionAccessEnabled: defaultLocation.class_access_group ?? false,
        },
        isSessionAccess: value.isSessionAccess ?? false,
        setIsSessionAccess: value.setIsSessionAccess,
      }}>
      {children}
    </LocationContext.Provider>
  );
};

export const useLocationContext = () => useContext(LocationContext);
